import { useEffect, useRef, useState } from "react";
const SAMPLE_L = { "deal_terms.json": "Condiciones del crédito de ejemplo", "expected_cfdi_totals.json": "Totales CFDI esperados" };
import { Upload, FileArchive, Download, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, BACKEND_URL } from "../../lib/api";

const ACCEPT = ".pdf,.xlsx,.xlsm,.csv,.xml,.zip,.jpg,.jpeg,.png,.heic,.heif,.tif,.tiff,.webp";

export const UploadPanel = ({ onQueued, onClear }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rfc, setRfc] = useState("");
  const [samples, setSamples] = useState([]);

  useEffect(() => {
    http.get("/poc/samples").then((r) => setSamples(r.data)).catch(() => setSamples([]));
  }, []);

  const send = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    if (rfc.trim()) fd.append("rfc", rfc.trim());
    setBusy(true);
    try {
      const r = await http.post("/poc/upload", fd);
      toast.success(`${r.data.jobs.length} archivo(s) en proceso`);
      onQueued();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir archivos");
    } finally {
      setBusy(false);
    }
  };

  const loadSamples = async () => {
    setBusy(true);
    try {
      const r = await http.post("/poc/load-samples");
      toast.success(`${r.data.job_ids.length} archivos de muestra encolados`);
      onQueued();
    } catch {
      toast.error("No se pudieron cargar las muestras");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel p-6 lg:p-8" data-testid="upload-panel">
      <p className="eyebrow">Documentos de prueba</p>
      <h2 className="panel-title mt-1 text-xl sm:text-2xl">Prueba Risk AI Engine con cualquier documento</h2>
      <p className="mt-2 text-sm text-zinc-500">Ekatena (PDF/XLSX), ZIP CIEC con CFDI XML, constancia, opinión 32-D, EEFF y balanza.</p>

      <div
        data-testid="file-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files); }}
        className={`grain mt-6 flex cursor-pointer flex-col items-start gap-3 border-2 border-dashed p-8 transition-colors duration-200 ${drag ? "border-orange-500 bg-orange-50" : "border-zinc-300 hover:border-zinc-900"}`}
      >
        {busy ? <Loader2 className="animate-spin text-orange-500" /> : <Upload className="text-orange-500" />}
        <p className="text-sm font-semibold">Arrastra archivos aquí o haz clic para seleccionar</p>
        <p className="font-mono text-xs text-zinc-500">PDF · XLSX · CSV · XML · ZIP</p>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} hidden data-testid="file-input" onChange={(e) => { send(e.target.files); e.target.value = ""; }} />
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[180px]">
          <span className="eyebrow">RFC del expediente (opcional)</span>
          <input data-testid="rfc-input" value={rfc} onChange={(e) => setRfc(e.target.value.toUpperCase())} placeholder="Se infiere de los CFDI"
            className="mt-1 w-full border border-zinc-300 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-orange-500" />
        </label>
        <button data-testid="cargar-archivos-muestra-button" disabled={busy} onClick={loadSamples} className="btn-orange flex items-center gap-2 px-5 py-2.5 text-sm font-semibold">
          <FileArchive size={16} /> Cargar archivos de muestra
        </button>
        <button data-testid="limpiar-archivos-button" onClick={onClear} className="btn-ghost flex items-center gap-2 border border-zinc-300 px-4 py-2.5 text-sm">
          <Trash2 size={15} /> Limpiar
        </button>
      </div>

      <details className="mt-6 text-sm" data-testid="sample-files-list">
        <summary className="cursor-pointer text-zinc-600 hover:text-orange-600">Descargar archivos de muestra ({samples.length})</summary>
        <ul className="mt-3 space-y-1.5">
          {samples.map((s) => (
            <li key={s.name}>
              <a className="flex items-center gap-2 text-xs text-zinc-700 hover:text-orange-600" href={`${BACKEND_URL}${s.url}`} data-testid={`sample-file-${s.name}`}>
                <Download size={12} /> {SAMPLE_L[s.name] || s.name.replace(/[_-]/g, " ").replace(/\.\w+$/, "")} <span className="text-zinc-400">({(s.size / 1024).toFixed(0)} KB)</span>
              </a>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
};
